/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import CryptoJS from 'crypto-js'

// @components
import DashboardNavbar from '../../../components/dashboard/DashboardNavbar'

// @services
import { SubscriptionService } from '../../../services/subscription.service'
import companyService from '../../../services/company.service'

// @domain
import { IPaymentsList, ITypesDocumentList } from '../../../domain/interfaces/Subscription'

// @utils
import { COMPANY_STATUS_PENDING, COMPANY_STATUS_PENDING_ADMIN } from '../../../utils/ConstantsUtils'

const CompanySubscription: React.FC = () => {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSubscriptionForm, setShowSubscriptionForm] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false);
    const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'xervix-default-key-2025-2100';
    const [typesDocument, setTypesDocument] = useState<ITypesDocumentList[]>([]);
    const companyId = window.localStorage.getItem('company_id') || '';
    const [hasError, setHasError] = useState(false);
    const [paymentHistory, setPaymentHistory] = useState<IPaymentsList[]>([]);

    const initialValues = {
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        docType: '',
        docNumber: ''
    }

    const validationSchema = Yup.object({
        cardNumber: Yup.string()
            .required('El número de tarjeta es requerido')
            .matches(/^\d{4}\s\d{4}\s\d{4}\s\d{4}$/, 'El número de tarjeta debe tener 16 dígitos'),
        expiryDate: Yup.string()
            .required('La fecha de vencimiento es requerida')
            .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Formato inválido (MM/AA)'),
        cvv: Yup.string()
            .required('El CVV es requerido')
            .matches(/^\d{3,4}$/, 'El CVV debe tener 3 o 4 dígitos'),
        docType: Yup.string()
            .required('El tipo de documento es requerido'),
        docNumber: Yup.string()
            .required('El número de documento es requerido')
    })

    const fetchTypesDocument = async () => {
        try {
            const response = await SubscriptionService.getTypesDocument();
            setTypesDocument(response.data.data);
            //toast.success('Tipos de documento cargados correctamente');
        } catch (error) {
            toast.error('Error al cargar los tipos de documento');
        }
    }

    const fetchSubscription = async () => {
        if (!companyId) {
            setHasError(true);
            setLoading(false);
            return toast.error('No se pudo acceder a la suscripción, por favor inicia sesión nuevamente mas tarde');
        }
        try {
            setLoading(true);
            const response = await SubscriptionService.getSubscription(companyId);
            setIsSubscribed(response.data.hasSubscription);
            if (response.data.hasSubscription) toast.success('Suscripción cargada correctamente');
            setLoading(false);
            setHasError(false);
        } catch (error) {
            setHasError(true);
            setLoading(false);
            toast.error('En el momento no es posible cargar la suscripción, por favor intenta más tarde');
        }
    }

    const fetchPayments = async () => {
        if (!companyId) return;
        try {
            const response = await SubscriptionService.getPayments(companyId);
            setPaymentHistory(response.data);
            toast.success('Pagos cargados correctamente');
        } catch (error) {
            toast.error('En el momento no es posible cargar los pagos, por favor intenta más tarde');
        }
    }

    useEffect(() => {
        fetchSubscription();
        fetchPayments();
        fetchTypesDocument();
    }, [])

    const formatCardNumber = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        return numbers.replace(/(.{4})/g, '$1 ').trim()
    }

    // Función para formatear fecha de vencimiento
    const formatExpiryDate = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length >= 2) {
            return numbers.substring(0, 2) + '/' + numbers.substring(2, 4)
        }
        return numbers
    }

    const formatCvv = (value: string) => {
        return value.replace(/\D/g, '')
    }

    const formatDocNumber = (value: string) => {
        return value.replace(/\D/g, '')
    }

    const handleToken = async (values: typeof initialValues) => {
        try {
            const payload = {
                cardNumber: values.cardNumber.replace(/\s/g, ''),
                expYear: `20${values.expiryDate.split("/")[1]}`,
                expMonth: values.expiryDate.split("/")[0],
                cvc: values.cvv,
                docType: values.docType,
                docNumber: values.docNumber,
                companyId: companyId
            };

            const payloadString = JSON.stringify(payload);
            const encryptedPayload = CryptoJS.AES.encrypt(payloadString, encryptionKey).toString();
            return encryptedPayload;
        } catch (error) {
            toast.error('Error al procesar los datos de la tarjeta');
            throw error;
        }
    }

    // Función de envío del formulario con Formik
    const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: any) => {
        try {
            const encryptedPayload = await handleToken(values);
            const subscriptionData = { encryptedPayload: encryptedPayload };
            const response = await SubscriptionService.createSubscription(subscriptionData);
            const message = response?.data?.message || 'Suscripción activada correctamente';
            handleResetForm(message, resetForm);
        } catch (error) {
            toast.error('No se pudo hacer la suscripción. Por favor, verifica los datos de tu tarjeta.');
            setSubmitting(false);
        }
    }

    const changeCompanyStatus = async (status: string) => {
        try {
            await companyService.changeCompanyState(companyId, status);
            toast.success('Estado de la empresa cambiado correctamente');
        } catch (error) {
            toast.error('Error al cambiar el estado de la empresa');
        }
    }

    const handleResetForm = (message: string, resetForm: any) => {
        toast.success(message);
        fetchSubscription();
        changeCompanyStatus(COMPANY_STATUS_PENDING_ADMIN);
        setShowSubscriptionForm(false)
        resetForm()
    }

    const handleCancelSubscription = async () => {
        if (!companyId) return toast.error('No se pudo acceder a la suscripción, por favor inicia sesión nuevamente mas tarde');
        try {
            setLoading(true);
            await SubscriptionService.cancelSubscription(companyId);
            setIsSubscribed(false)
            setShowCancelModal(false)
            setLoading(false);
            changeCompanyStatus(COMPANY_STATUS_PENDING);
            toast.success('Suscripción cancelada correctamente');
        } catch (error: any) {
            if (error.status === 400 && error.data.message) return toast.error(error.data.message)
            toast.error('Error al cancelar la suscripción')
        }
    }

    const getStatusBadge = (status: string) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        if (status === 'Aprobada') return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
        if (status === 'pendiente') return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
        if (status === 'pendingPayment') return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
        if (status === 'Rechazada') return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }

    return (
        <div className="w-full min-h-screen bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark">
            <DashboardNavbar hasSubscription={true} />

            <div className="container mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Gestión de Suscripción
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        Administra tu suscripción premium y revisa tu historial de pagos
                    </p>
                </div>

                {/* Estado actual de la suscripción */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Estado de Suscripción
                            </h2>
                            <p className="mt-1 text-gray-600 dark:text-gray-400">
                                {hasError
                                    ? 'No es posible cargar la suscripción, por favor intenta más tarde'
                                    : isSubscribed
                                        ? 'Tu suscripción está activa'
                                        : 'No tienes una suscripción activa'}
                            </p>
                        </div>
                        <div className="flex space-x-4">
                            {hasError ? (
                                <button
                                    onClick={() => fetchSubscription()}
                                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Reintentar
                                </button>
                            ) : !isSubscribed ? (
                                <button
                                    onClick={() => setShowSubscriptionForm(true)}
                                    disabled={loading}
                                    className="bg-xervix-primary hover:bg-xervix-primary-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                >
                                    {loading ? 'Cargando...' : 'Suscribirse'}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Cancelar Suscripción
                                </button>
                            )
                            }
                        </div>
                    </div>
                </div>

                {/* Formulario de suscripción */}
                {
                    showSubscriptionForm && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                Información de Pago
                            </h2>

                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ isSubmitting, setFieldValue }) => (
                                    <Form className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="col-span-1 md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Número de Tarjeta
                                                </label>
                                                <Field name="cardNumber">
                                                    {({ field }: any) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            placeholder="1234 5678 9012 3456"
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                            maxLength={19}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const formatted = formatCardNumber(e.target.value)
                                                                setFieldValue('cardNumber', formatted)
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <ErrorMessage name="cardNumber" component="div" className="text-red-500 text-sm mt-1" />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Fecha de Vencimiento
                                                </label>
                                                <Field name="expiryDate">
                                                    {({ field }: any) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            placeholder="MM/AA"
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                            maxLength={5}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const formatted = formatExpiryDate(e.target.value)
                                                                setFieldValue('expiryDate', formatted)
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <ErrorMessage name="expiryDate" component="div" className="text-red-500 text-sm mt-1" />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    CVV
                                                </label>
                                                <Field name="cvv">
                                                    {({ field }: any) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            placeholder="123"
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                            maxLength={4}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const formatted = formatCvv(e.target.value)
                                                                setFieldValue('cvv', formatted)
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <ErrorMessage name="cvv" component="div" className="text-red-500 text-sm mt-1" />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Tipo de Documento
                                                </label>
                                                <Field name="docType">
                                                    {({ field }: any) => (
                                                        <select
                                                            {...field}
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                        >
                                                            <option value="">Seleccione tipo</option>
                                                            {typesDocument.map((type) => (
                                                                <option key={type.id} value={type.cod}>{type.name}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </Field>
                                                <ErrorMessage name="docType" component="div" className="text-red-500 text-sm mt-1" />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Número de Documento
                                                </label>
                                                <Field name="docNumber">
                                                    {({ field }: any) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            placeholder="Ingrese su número de documento"
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const formatted = formatDocNumber(e.target.value)
                                                                setFieldValue('docNumber', formatted)
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <ErrorMessage name="docNumber" component="div" className="text-red-500 text-sm mt-1" />
                                            </div>
                                        </div>

                                        <div className="flex justify-end space-x-4">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="bg-xervix-primary hover:bg-xervix-primary-dark disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                            >
                                                {isSubmitting ? 'Procesando...' : 'Confirmar Suscripción'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowSubscriptionForm(false)}
                                                className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg font-medium transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    )
                }

                {/* Modal de confirmación de cancelación */}
                {
                    showCancelModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Cancelar Suscripción
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    ¿Estás seguro de que deseas cancelar tu suscripción? Perderás acceso a todas las funcionalidades premium.
                                </p>
                                <div className="flex space-x-4">
                                    <button
                                        onClick={handleCancelSubscription}
                                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        Sí, Cancelar
                                    </button>
                                    <button
                                        onClick={() => setShowCancelModal(false)}
                                        className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        No, Mantener
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Historial de pagos */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Historial de Pagos
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Fecha
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Descripción
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Monto
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Estado
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {paymentHistory?.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {new Date(payment.paymentDate).toLocaleDateString('es-ES')}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                            Pago suscripción
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                            ${payment?.amountDue?.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={getStatusBadge(payment.transactionStatus)}>
                                                {payment.transactionStatus === 'Aprobada' && 'Pagado'}
                                                {payment.transactionStatus === 'Pendiente' && 'Pendiente'}
                                                {payment.transactionStatus === 'Rechazada' && 'Rechazada'}
                                                {payment.transactionStatus === 'pendingPayment' && 'Pendiente de pago'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {paymentHistory.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 dark:text-gray-400">
                                No hay historial de pagos disponible
                            </p>
                        </div>
                    )}
                </div>
            </div >
        </div >
    )
}

export default CompanySubscription